import type {
  AdminMaterialResponse,
  AdminMaterialsResponse,
  CreateAdminMaterialBody,
  PatchAdminMaterialBody,
  UpdateAdminMaterialBody,
} from '@/api/(admin)/materials/types';
import { API_ROOT_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';

/**
 * Pure RPC over the admin materials endpoints, all served by
 * `/api/admin/materials` — so each takes `API_ROOT_URL`; `apiFetch`'s default
 * base would put `/api-service` in front of the path.
 */

/**
 * The list. Takes a pre-built query string; paging, sort, search and filter
 * live in the hook. Answers the paginated `{ data, metadata }` envelope.
 */
export async function fetchAdminMaterials(
  qs: string,
): Promise<AdminMaterialsResponse> {
  return apiFetch<AdminMaterialsResponse>(
    `/api/admin/materials?${qs}`,
    undefined,
    API_ROOT_URL,
  );
}

/**
 * One material, including `imageFileUrl` and `translations`. Answers the
 * `{ data, metadata }` envelope; the hook unwraps it.
 */
export async function fetchAdminMaterial(
  id: string,
): Promise<AdminMaterialResponse> {
  return apiFetch<AdminMaterialResponse>(
    `/api/admin/materials/${encodeURIComponent(id)}`,
    undefined,
    API_ROOT_URL,
  );
}

/**
 * Creates a material. Answers 201 with the `{ data, metadata }` envelope;
 * nothing here reads it — the hook refetches.
 */
export async function createAdminMaterial(
  body: CreateAdminMaterialBody,
): Promise<AdminMaterialResponse> {
  return apiFetch<AdminMaterialResponse>(
    '/api/admin/materials',
    { method: 'POST', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}

/**
 * A PUT. `name` is required, so this is a full replacement. Answers the
 * `{ data, metadata }` envelope; nothing here reads it — the hook refetches.
 */
export async function updateAdminMaterial(
  id: string,
  body: UpdateAdminMaterialBody,
): Promise<AdminMaterialResponse> {
  return apiFetch<AdminMaterialResponse>(
    `/api/admin/materials/${encodeURIComponent(id)}`,
    { method: 'PUT', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}

/**
 * A partial update, merged over the stored row. Cannot change the name.
 * Answers the `{ data, metadata }` envelope; nothing here reads it — the hook
 * refetches.
 */
export async function patchAdminMaterial(
  id: string,
  body: PatchAdminMaterialBody,
): Promise<AdminMaterialResponse> {
  return apiFetch<AdminMaterialResponse>(
    `/api/admin/materials/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}
